import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowUpRight } from "react-icons/fi";
import "./BoardsTypes.css";
import { useTranslation } from "react-i18next";
import { getBoards } from "../../../../../api/boards";
import { resolveMediaUrl } from "../../../../../utils/media";

import imgRegular from "../../../../../assets/images/allproducts/boards/regular.png";
import imgMousture from "../../../../../assets/images/allproducts/boards/mousture.png";
import imgMoisture from "../../../../../assets/images/allproducts/boards/moisture.png";
import imgFire from "../../../../../assets/images/allproducts/boards/fire.png";
import imgFmr from "../../../../../assets/images/allproducts/boards/fmr.png";
import imgExterior from "../../../../../assets/images/allproducts/boards/exterior.png";

const boardTypes = [
  {
    id: "regular",
    title: "Regular Light Weight",
    description:
      "Regular gypsum board is a factory-manufactured, paper-faced gypsum plasterboard designed for general internal lining applications.",
    image: imgRegular,
  },
  {
    id: "mousture",
    title: "Mousture",
    description:
      "Aquaguard gypsum board is a factory-manufactured gypsum plasterboard engineered for internal walls in humid environments.",
    image: imgMousture,
  },
  {
    id: "moisture",
    title: "Moisture",
    description:
      "AL AHLI H3 MR gypsum board is a factory-manufactured gypsum plasterboard engineered for internal walls with moisture resistance.",
    image: imgMoisture,
  },
  {
    id: "fire",
    title: "Fire",
    description:
      "Fire Resistant (FR) gypsum board is a factory-manufactured, paper-faced gypsum plasterboard with a non-combustible core.",
    image: imgFire,
  },
  {
    id: "fmr",
    title: "FMR",
    description:
      "Fire & Moisture Resistant Gypsum Board (FMR) is a factory-manufactured, paper-faced gypsum plasterboard engineered for demanding conditions.",
    image: imgFmr,
  },
  {
    id: "exterior",
    title: "Exterior",
    description:
      "ShieldEx Glass Mat Gypsum Sheathing Board is an exterior-grade gypsum panel with fiberglass mat facings and a non-combustible core.",
    image: imgExterior,
  },
];

const slugifyBoard = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeBoardName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const BOARD_RENDER_CONFIG = [
  { label: "Regular Board", aliases: ["regular board", "regular", "regular light weight"] },
  { label: "AL AHLI H3 MR", aliases: ["al ahli h3 mr", "h3 mr", "moisture"] },
  { label: "AQUAGUARD", aliases: ["aquaguard", "mousture"] },
  { label: "Fire Board", aliases: ["fire board", "fire"] },
  { label: "FMR Board", aliases: ["fmr boards", "fmr board", "fmr"] },
  { label: "Shieldex", aliases: ["shieldex", "exterior"] },
];

const getBoardRenderConfigIndex = (title) => {
  const normalizedTitle = normalizeBoardName(title);
  return BOARD_RENDER_CONFIG.findIndex((config) =>
    config.aliases.some((alias) => normalizedTitle.includes(normalizeBoardName(alias)))
  );
};

const BoardsTypes = () => {
  const { i18n } = useTranslation();
  const [apiBoards, setApiBoards] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadBoards = async () => {
      try {
        const data = await getBoards({ language: String(i18n.language || "en").toLowerCase() });
        if (isMounted) {
          setApiBoards(Array.isArray(data) ? data : []);
        }
      } catch (_error) {
        if (isMounted) {
          setApiBoards([]);
        }
      }
    };

    loadBoards();
    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  const cards = useMemo(() => {
    if (!Array.isArray(apiBoards) || !apiBoards.length) return boardTypes;

    const mapped = apiBoards
      .map((item) => {
        const rawSlug =
          item?.slug || item?.boardType || item?.type || item?.name || item?.title || item?.id;
        const slug = slugifyBoard(rawSlug);
        if (!slug) return null;

        const image = resolveMediaUrl(item?.imageUrl);
        const resolvedTitle = item?.name || item?.title || item?.boardType || "Board";
        const configIndex = getBoardRenderConfigIndex(resolvedTitle);
        const renderedTitle = configIndex >= 0 ? BOARD_RENDER_CONFIG[configIndex].label : resolvedTitle;

        return {
          id: slug,
          title: renderedTitle,
          description:
            item?.heroDescription ||
            "Gypsum board technical details are available in the product page.",
          image: image || "",
        };
      })
      .filter(Boolean);

    mapped.sort((a, b) => {
      const aIndex = getBoardRenderConfigIndex(a.title);
      const bIndex = getBoardRenderConfigIndex(b.title);
      const aRank = aIndex >= 0 ? aIndex : Number.MAX_SAFE_INTEGER;
      const bRank = bIndex >= 0 ? bIndex : Number.MAX_SAFE_INTEGER;
      if (aRank !== bRank) return aRank - bRank;
      return normalizeBoardName(a.title).localeCompare(normalizeBoardName(b.title));
    });

    return mapped.length ? mapped : boardTypes;
  }, [apiBoards]);

  return (
    <section className="boardsTypes" aria-labelledby="boardsTypes-heading">
      <div className="boardsTypes-inner">
        <div className="boardsTypes-grid">
          {cards.map((item) => (
            <Link
              key={item.id}
              className="boardsTypes-card"
              to={`/products/boards/${item.id}`}
            >
              <div className="boardsTypes-imageWrap">
                {item.image ? <img src={item.image} alt="" draggable={false} /> : null}
              </div>
              <h3 className="boardsTypes-cardTitle">{item.title}</h3>
              <p className="boardsTypes-cardDesc">{item.description}</p>
              <span className="boardsTypes-open">
                Open product
                <FiArrowUpRight aria-hidden />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BoardsTypes;
